import React, { useCallback, useEffect, useRef, useState } from "react";

import { Download, FileAudio, Upload, X } from "lucide-react";
import { useDispatch } from "react-redux";

import { startTranscriptionSummaryTask } from "@/redux/transcription-summary/index";
import { discardRecording, releaseRecording } from "@/services/recordingOwnership";
import { store } from "@/store";

import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@components/ui/dialog";
import { Label } from "@components/ui/label";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";

import type { ApiError } from "@utils/callApi";
import { showError } from "@utils/notification";
import { cn } from "@utils/ui";
import type { UploadProgress } from "@utils/uploadWithProgress";

const ACCEPTED_EXTENSIONS = ".mp3,.wav,.mp4,.m4a";
const MAX_SIZE_BYTES = 500 * 1024 * 1024; // 500MB (matches the modal's hint)

// Only 2 and 3 have a defined role list on the batch diarization side
// (transcribe-summarize-api's speaker_roles.py DEFAULT_ROLE_ORDER_BY_COUNT) --
// no point offering a count the backend can't do anything useful with.
type ExpectedSpeakerCount = 2 | 3 | null;

/** Human-readable megabytes, e.g. 40265318 → "38.4 MB". */
const formatMb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

type AudioUploaderProps = {
  caseNumber: string;
  examinee_name: string; // Add examinee_name as a required prop
  disabled?: boolean;
  onPrepareUpload?: () => Promise<string>;
  onRequestOverwriteConfirm?: () => Promise<boolean> | boolean;
};
export const AudioUploader = ({
  caseNumber,
  examinee_name,
  disabled,
  onRequestOverwriteConfirm,
  onPrepareUpload,
}: AudioUploaderProps) => {
  const dispatch = useDispatch<typeof store.dispatch>();

  const submissionIdRef = useRef<string | null>(null);
  const uploadingRef = useRef(false);
  const mountedRef = useRef(true);
  // Leaving with a reservation but no upload in flight frees it; an in-flight upload
  // still needs it, and its failure path frees it instead.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (submissionIdRef.current && !uploadingRef.current)
        void discardRecording().catch(() => {});
    };
  }, []);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modal + upload state (MDRS-113 Upload Recording modal).
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [expectedSpeakerCount, setExpectedSpeakerCount] =
    useState<ExpectedSpeakerCount>(null);

  // Open the modal — gated by the same name check + overwrite confirmation the
  // header/empty-state CTAs used before the modal existed.
  const handleOpen = useCallback(async () => {
    if (disabled || isUploading) return;

    if (!examinee_name.trim()) {
      showError("Examinee name is required for transcription", dispatch);
      return;
    }

    if (onRequestOverwriteConfirm) {
      const shouldContinue = await onRequestOverwriteConfirm();
      if (!shouldContinue) return;
    }

    setSelectedFile(null);
    setProgress(null);
    setExpectedSpeakerCount(null);
    setIsOpen(true);
  }, [disabled, dispatch, examinee_name, isUploading, onRequestOverwriteConfirm]);

  const validateFile = useCallback(
    (file: File) => {
      const name = file.name.toLowerCase();
      const okExtension = ACCEPTED_EXTENSIONS.split(",").some((ext) =>
        name.endsWith(ext.trim())
      );
      if (!okExtension) {
        showError("Please upload an MP3, MP4, WAV, or M4A file", dispatch);
        return false;
      }
      if (file.size > MAX_SIZE_BYTES) {
        showError("File size should not exceed 500MB", dispatch);
        return false;
      }
      return true;
    },
    [dispatch]
  );

  const handleFileSelected = useCallback(
    (file: File | undefined | null) => {
      if (!file) return;
      if (!validateFile(file)) return;
      setSelectedFile(file);
    },
    [validateFile]
  );

  const handleUpload = useCallback(async () => {
    if (!selectedFile || isUploading) return;

    setIsUploading(true);
    uploadingRef.current = true;
    setProgress({ loaded: 0, total: selectedFile.size, percent: 0 });

    const formData = new FormData();
    formData.append("files", selectedFile, selectedFile.name);
    formData.append("case_number", caseNumber);
    formData.append("examinee_name", examinee_name);
    if (expectedSpeakerCount != null) {
      formData.append("expected_speaker_count", String(expectedSpeakerCount));
    }

    try {
      try {
        submissionIdRef.current ??= (await onPrepareUpload?.()) ?? null;
      } catch (error) {
        // Nothing was uploaded, and the thunk that toasts errors never ran.
        setProgress(null);
        showError(
          (error as ApiError).detail ?? "Couldn't start the upload. Try again.",
          dispatch
        );
        return;
      }
      if (submissionIdRef.current)
        formData.append("submission_id", submissionIdRef.current);
      await dispatch(
        startTranscriptionSummaryTask(formData, {
          onUploadProgress: setProgress,
        })
      );
      // The thunk starts polling + surfaces its own success toast; close the
      // modal once the bytes are up and the task is queued.
      releaseRecording();
      submissionIdRef.current = null;
      setIsOpen(false);
    } catch {
      // The thunk shows the error toast; keep the modal open so the user can
      // retry or cancel. Nobody can retry after unmount, so free the reservation.
      if (!mountedRef.current && submissionIdRef.current) {
        submissionIdRef.current = null;
        void discardRecording().catch(() => {});
      }
    } finally {
      setIsUploading(false);
      uploadingRef.current = false;
    }
  }, [
    caseNumber,
    dispatch,
    examinee_name,
    expectedSpeakerCount,
    isUploading,
    selectedFile,
    onPrepareUpload,
  ]);

  // Block dismissing the modal mid-upload (the request would keep running).
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isUploading) return;
      setIsOpen(open);
      if (!open) {
        if (submissionIdRef.current) void discardRecording().catch(() => {});
        submissionIdRef.current = null;
        setSelectedFile(null);
        setProgress(null);
        setExpectedSpeakerCount(null);
      }
    },
    [isUploading]
  );

  const percent = progress?.percent ?? 0;

  return (
    <div>
      {/* simple-outline (gray). Always sm — header and empty-state must
          render the same button size (previously "default" in the empty
          state, mismatching the header's "sm" Start Session button). */}
      <Button
        variant="simple-outline"
        size="sm"
        disabled={disabled}
        className="flex items-center gap-1.5"
        onClick={handleOpen}
      >
        <Upload />
        Upload Recording
      </Button>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="min-w-[600px] max-w-lg gap-6 p-6"
          onInteractOutside={(e) => isUploading && e.preventDefault()}
          onEscapeKeyDown={(e) => isUploading && e.preventDefault()}
        >
          <div className="flex items-start justify-between">
            <DialogTitle className="text-2xl font-semibold text-background-dark">
              Upload Recording
            </DialogTitle>
            <DialogDescription className="sr-only">
              Upload an existing audio recording to transcribe and summarise for this
              case. Supported formats: MP3, MP4, WAV, M4A. Max size 500MB.
            </DialogDescription>
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              disabled={isUploading}
              aria-label="Close"
              className="text-foreground-muted transition-colors hover:text-foreground disabled:opacity-40"
            >
              <X className="size-6" />
            </button>
          </div>

          {isUploading || (progress && selectedFile) ? (
            // ── Uploading state ──
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-primary-50 text-primary">
                  <FileAudio className="size-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {selectedFile?.name}
                  </p>
                  <p className="text-xs text-foreground-muted">
                    Uploading audio file...
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-primary-50">
                  <div
                    className="h-full rounded-full bg-primary transition-[width]"
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-foreground-muted">
                  <span>
                    {percent}% - {formatMb(progress?.loaded ?? 0)} of{" "}
                    {formatMb(progress?.total ?? selectedFile?.size ?? 0)}
                  </span>
                  <span>Uploading...</span>
                </div>
              </div>
            </div>
          ) : (
            // ── Default (dropzone) state ──
            <div className="flex flex-col gap-4">
              <div>
                {/* Matches CreateCaseModal's file field label (FieldWrapper) —
                    label + required asterisk, muted sublabel underneath. */}
                <Label
                  htmlFor="audio-recording-file-input"
                  className="block text-sm font-semibold"
                >
                  Upload Recording <span className="text-destructive">*</span>
                </Label>
                <p className="text-muted-foreground text-xs">
                  Upload an audio file (.mp3, .wav, .mp4, .m4a)
                </p>
              </div>

              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  handleFileSelected(e.dataTransfer.files?.[0]);
                }}
                className={cn(
                  "flex flex-col items-center gap-4 rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors",
                  "hover:border-primary-400 hover:bg-primary-50",
                  isDragging ? "border-primary bg-primary-50" : "border-border"
                )}
              >
                <span className="grid size-9 place-items-center rounded-full bg-success-50 text-success-600">
                  <Download className="size-4" />
                </span>
                {selectedFile ? (
                  <p className="text-sm text-foreground">
                    <span className="font-medium">{selectedFile.name}</span>
                  </p>
                ) : (
                  <div>
                    <p className="text-lg font-medium text-foreground">
                      Drag and drop your recording here
                    </p>
                    <p className="text-sm text-foreground-muted">
                      Supported file formats: MP3, MP4, WAV, M4A (max: 500 MB)
                    </p>
                  </div>
                )}
                <Button
                  className="mt-4"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Select File
                </Button>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground">
                  How many people are speaking? (optional)
                </span>
                <div className="flex flex-row items-center gap-6">
                  <RadioGroup
                    value={
                      expectedSpeakerCount != null
                        ? String(expectedSpeakerCount)
                        : ""
                    }
                    onValueChange={(val) =>
                      setExpectedSpeakerCount(val ? (Number(val) as 2 | 3) : null)
                    }
                    className="flex flex-row items-center gap-6"
                  >
                    <label
                      htmlFor="expected-speaker-count-2"
                      className="flex cursor-pointer items-center space-x-2"
                    >
                      <RadioGroupItem value="2" id="expected-speaker-count-2" />
                      <span className="text-sm">2</span>
                    </label>
                    <label
                      htmlFor="expected-speaker-count-3"
                      className="flex cursor-pointer items-center space-x-2"
                    >
                      <RadioGroupItem value="3" id="expected-speaker-count-3" />
                      <span className="text-sm">3 (with interpreter)</span>
                    </label>
                  </RadioGroup>
                  <button
                    type="button"
                    onClick={() => setExpectedSpeakerCount(null)}
                    className="text-sm text-foreground-muted underline-offset-2 hover:underline"
                  >
                    Not sure
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button
              variant="simple-outline"
              disabled={isUploading}
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={!selectedFile || isUploading}
              isLoading={isUploading}
              onClick={handleUpload}
            >
              {isUploading ? "Processing..." : "Upload Recording"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <input
        id="audio-recording-file-input"
        type="file"
        ref={fileInputRef}
        onChange={(e) => handleFileSelected(e.target.files?.[0])}
        accept={ACCEPTED_EXTENSIONS}
        style={{ display: "none" }}
      />
    </div>
  );
};
